import type { FC } from 'react';

import { Textfit } from '@wwe-portal/ui/components/text-fit';

import { ClickableDiv } from '@wwe-portal/ui/components/button';
import { SoldOut } from '@wwe-portal/ui/features/store-revamp';
import lockIconWebp from '@wwe-portal/ui/assets/images/flagship/lock-icon.webp';

import { OfferTimeCountDown } from '../component/offer-time-countdown';
import { NotifPip } from '../flagship/notif-pip';
import { type OfferCardProps } from '../type';
import { OfferCardTypeEnum } from '@wwe-portal/entities/offer.type';

import { OfferHeader } from './offer-header';
import { OfferItems } from './offer-items';
import { OfferLayout } from './offer-layout';
import { ResponseOfferCardBackground } from './responsive-offer-card-background';
import { StockQuantity } from './stock-quantity';

export const OfferSingle3Card: FC<OfferCardProps> = ({
  id,
  backgroundImage,
  dynamicBackgroundImage,
  title,
  price,
  strikethroughPrice,
  endDate,
  items,
  quantity,
  maxQuantity,
  style,
  onClick,
  onPurchaseClick,
  overlay,
  hasPip,
  hasContest,
  badgeIcon,
  badgeText,
  type,
  isBlocked = false,
  showStock,
  soldOut,
}) => {
  return (
    <div className="relative" data-id={id}>
      <ClickableDiv
        className="aspect-single-3 font-poppins rounded-b-[calc(600%/108)]"
        onClick={onClick}
        style={style}
      >
        <div className="absolute inset-0 overflow-clip rounded-b-[10px]">
          <div className="absolute -bottom-[calc(400%/167)] -left-[calc(600%/108)] -right-[calc(600%/108)] -top-[calc(400%/167)]">
            <ResponseOfferCardBackground
              backgroundImage={backgroundImage}
              dynamicBackgroundImage={dynamicBackgroundImage}
              type={type}
            />
          </div>
        </div>
        <OfferLayout
          cardType={OfferCardTypeEnum.SINGLE3}
          footer={
            <>
              {price != null && (
                <ClickableDiv
                  className="h-full w-full px-1"
                  data-slot="price"
                  onClick={onPurchaseClick}
                >
                  <Textfit
                    className="flex h-full w-full items-center justify-center text-center font-semibold capitalize text-white"
                    max={16}
                    mode="single"
                  >
                    <div className="drop-shadow-text-2 flex items-center justify-center gap-x-1">
                      {price}
                    </div>
                    {Boolean(isBlocked) && (
                      <img
                        alt="locked"
                        className="absolute inset-y-[17%] right-1 h-[21px] md:h-[22px]"
                        src={lockIconWebp}
                      />
                    )}
                  </Textfit>
                </ClickableDiv>
              )}
              {strikethroughPrice != null && (
                <div className="absolute inset-x-0 -top-1 flex justify-center">
                  <div className="text-2xs -translate-y-1/2 text-left font-semibold text-[#fffbe3] line-through">
                    {strikethroughPrice}
                  </div>
                </div>
              )}
            </>
          }
          header={<OfferHeader maxFont={14}>{title}</OfferHeader>}
          items={
            <OfferItems itemWidth="calc(4000%/108)" items={items} maxItem={1} />
          }
          stockQuantity={
            <StockQuantity
              badgeIcon={badgeIcon}
              badgeText={badgeText}
              hasContest={hasContest}
              maxQuantity={maxQuantity}
              quantity={quantity}
              showStock={showStock && !soldOut}
              type={type}
            />
          }
          timeLeft={endDate ? <OfferTimeCountDown endDate={endDate} /> : null}
        />
        {overlay ? <div className="absolute inset-0">{overlay}</div> : null}
      </ClickableDiv>
      {hasPip && !soldOut ? <NotifPip /> : null}
      {soldOut && <SoldOut type={OfferCardTypeEnum.SINGLE3} />}
    </div>
  );
};
